import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { categories } from "../mock";
import { adminCreatePlace, adminUpdatePlace } from "../api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const blank = { category: "", name: "", type: "", area: "", note: "", rating: "", lat: "", lng: "", maps: "" };

const AdminPlaceDialog = ({ open, onOpenChange, place, onSaved }) => {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(place);

  useEffect(() => {
    if (place) {
      setForm({
        category: place.category || "",
        name: place.name || "",
        type: place.type || "",
        area: place.area || "",
        note: place.note || "",
        rating: place.rating ?? "",
        lat: place.lat ?? "",
        lng: place.lng ?? "",
        maps: place.maps || "",
      });
    } else {
      setForm(blank);
    }
  }, [place, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.category || !form.type || !form.area || !form.note) {
      return toast.error("name, category, type, area and note are required.");
    }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        name: form.name.trim(),
        type: form.type.trim(),
        area: form.area.trim(),
        note: form.note.trim(),
        rating: form.rating === "" ? null : parseFloat(form.rating),
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
        maps: form.maps.trim() || null,
      };
      const saved = editing
        ? await adminUpdatePlace(place.id, payload)
        : await adminCreatePlace(payload);
      toast.success(editing ? "spot updated." : "spot added.");
      onOpenChange(false);
      onSaved && onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit spot" : "Add spot"}</DialogTitle>
          <DialogDescription>{editing ? "amend any detail of this place." : "add a new place to dilli.wiki."}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Rating</Label>
              <Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => set("rating", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Input value={form.type} onChange={(e) => set("type", e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Neighbourhood</Label>
              <Input value={form.area} onChange={(e) => set("area", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Note</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Latitude</Label>
              <Input type="number" step="0.0001" value={form.lat} onChange={(e) => set("lat", e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Longitude</Label>
              <Input type="number" step="0.0001" value={form.lng} onChange={(e) => set("lng", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Google Maps URL (optional)</Label>
            <Input value={form.maps} onChange={(e) => set("maps", e.target.value)} placeholder="auto-generated if blank" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-neutral-900 text-white hover:bg-neutral-800">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? "Save changes" : "Add spot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPlaceDialog;
